import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
  Button,
  Alert,
  Avatar,
  Divider,
} from "@mui/material";
import {
  Message as MessageIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  PriorityHigh as HighPriorityIcon,
  Info as InfoIcon,
  Psychology as PsychologyIcon,
} from "@mui/icons-material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import MetricCard from "../shared/MetricCard";
import ChartContainer from "../shared/ChartContainer";
import dashboardService from "../../../../../services/dashboardService";
import NewColors from "../../../../../themes/NewColors";

const CommunicationModule = ({ organizationId }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // React Query for communication data with improved caching
  const {
    data,
    isLoading: loading,
    error,
    refetch,
    isRefetching: refreshing,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['dashboard-communication', organizationId],
    queryFn: async () => {
      const result = await dashboardService.getCommunicationSummary(organizationId);
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || "Failed to fetch communication data");
      }
    },
    enabled: !!organizationId,
    staleTime:            60 * 60 * 1000,
    gcTime:               4 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 3,
    // Removed initialData - it was preventing the actual API call
  });

  // Manual refresh handler
  const handleRefresh = () => {
    refetch();
  };

  // Format last updated time
  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString();
  };

  // Get risk color based on category (memoized)
  const getRiskColor = useCallback(
    (category) => {
      switch (category) {
        case "HIGH_MAINTENANCE":
          return theme.palette.warning.main;
        case "NON_COMMUNICATOR":
          return theme.palette.error.main;
        case "CHURNED":
          return theme.palette.error.dark;
        case "STABLE":
          return theme.palette.success.main;
        default:
          return theme.palette.grey[500];
      }
    },
    [theme],
  );

  // Get alert severity (memoized)
  const getAlertSeverity = useCallback((alertStatus) => {
    return alertStatus === "AT_RISK" ? "error" : "success";
  }, []);

  // Prepare chart data (memoized to prevent re-calculations on every render)
  const riskDistributionData = useMemo(
    () => {
      if (!data?.risk_distribution) return [];
      return Object.entries(data.risk_distribution).map(([key, value]) => ({
        name: key.replace("_", " ").toUpperCase(),
        value: value.count,
        percentage: value.percentage,
        color: getRiskColor(key.toUpperCase()),
      }));
    },
    [data?.risk_distribution, getRiskColor],
  );

  const messageTypeData = useMemo(
    () => {
      if (!data?.message_type_distribution) return [];
      return Object.entries(data.message_type_distribution).map(([key, value]) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        value: value.count,
        percentage: value.percentage,
      }));
    },
    [data?.message_type_distribution],
  );

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = useCallback(
    ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
      if (percent < 0.05) return null; // Don't show labels for slices smaller than 5%

      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text
          x={x}
          y={y}
          fill={NewColors.white}
          textAnchor={x > cx ? "start" : "end"}
          dominantBaseline="central"
          fontSize="12"
          fontWeight="bold"
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    },
    [],
  );

  console.log("CommunicationModule rendering...");

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with refresh */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Communication Analytics
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Track client communication patterns, engagement, and coach
            performance over the last 90 days
          </Typography>
          {dataUpdatedAt && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Last updated: {formatLastUpdated(dataUpdatedAt)}
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{ ml: 2 }}
        >
          {refreshing ? "Refreshing..." : "Refresh"}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message || "Failed to load communication data"}
        </Alert>
      )}

      {/* Communication Health Score */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <MetricCard
            title="Communication Health Score"
            value={data?.organization_summary?.communication_health_score || 0}
            format="percentage"
            icon={<PsychologyIcon />}
            variant={
              (data?.organization_summary?.communication_health_score || 0) >=
              70
                ? "success"
                : (data?.organization_summary?.communication_health_score ||
                      0) >= 50
                  ? "warning"
                  : "error"
            }
            subtitle="Overall communication effectiveness"
            description="Composite score based on communication frequency, engagement, response times, and client activity"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Clients"
            value={data?.organization_summary?.total_clients || 0}
            icon={<PersonIcon />}
            variant="default"
            subtitle={`${data?.organization_summary?.active_clients || 0} active`}
            description="Total number of clients in your organization"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="At Risk Clients"
            value={data?.organization_summary?.at_risk_clients || 0}
            icon={<WarningIcon />}
            variant="warning"
            subtitle="need immediate attention"
            description="Clients with communication gaps or low engagement requiring coach intervention (based on 90-day activity)"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Response Time"
            value={data?.communication_metrics?.avg_response_time_hours || 0}
            format="hours"
            icon={<ScheduleIcon />}
            variant={
              (data?.communication_metrics?.avg_response_time_hours || 0) <= 12
                ? "success"
                : (data?.communication_metrics?.avg_response_time_hours || 0) <=
                    24
                  ? "warning"
                  : "error"
            }
            subtitle="coach response time"
            description="Average time for coaches to respond to client messages"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Messages"
            value={data?.communication_metrics?.total_messages_sent || 0}
            icon={<MessageIcon />}
            variant="info"
            subtitle={`${data?.communication_metrics?.avg_messages_per_client || 0} per client`}
            description="Total messages sent by clients in the last 90 days"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Risk Distribution Chart */}
        <Grid item xs={12} md={6}>
          <ChartContainer
            title="Client Risk Distribution"
            subtitle="Breakdown of client behavioral patterns"
            loading={loading}
            height={400}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={riskDistributionData}
                  cx="50%"
                  cy="45%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={110}
                  fill={NewColors.active.primary}
                  dataKey="value"
                >
                  {riskDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value, name) => [
                    `${value} clients (${riskDistributionData.find((d) => d.name === name)?.percentage}%)`,
                    name,
                  ]}
                />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Grid>

        {/* Message Type Distribution */}
        <Grid item xs={12} md={6}>
          <ChartContainer
            title="Message Type Distribution"
            subtitle="Breakdown of communication methods"
            loading={loading}
            height={400}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={messageTypeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip
                  formatter={(value, name) => [
                    `${value} messages (${messageTypeData.find((d) => d.name === name)?.percentage}%)`,
                    "Count",
                  ]}
                />
                <Bar dataKey="value" fill={theme.palette.primary.main} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Grid>
      </Grid>

      {/* At Risk Clients Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <ChartContainer
            title="At Risk Clients"
            subtitle={`${data?.at_risk_clients?.length || 0} clients requiring immediate attention`}
            loading={loading}
            height={600}
          >
            <List
              sx={{ p: 0, maxHeight: "calc(100% - 40px)", overflowY: "auto" }}
            >
              {(data?.at_risk_clients || []).map((client) => (
                <ListItem
                  key={client.client_id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1.5,
                    flexDirection: "column",
                    alignItems: "stretch",
                    backgroundColor: "background.paper",
                    "&:hover": {
                      backgroundColor: "action.hover",
                      cursor: "pointer",
                    },
                  }}
                  onClick={() =>
                    navigate(
                      `/home/clients/summary/${client.client_id}/${organizationId}`,
                    )
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      mb: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        mr: 2,
                        bgcolor: getRiskColor(client.client_risk_category),
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {client.first_name} {client.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {client.risk_category_description}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Chip
                        label={client.alert_status}
                        color={getAlertSeverity(client.alert_status)}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {client.alert_reason}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Client metrics */}
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" color="secondary">
                          {client.combined_engagement_score}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Engagement Score
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" color="primary">
                          {client.communication_score}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Communication Score
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6">
                          {client.total_messages_sent}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Messages Sent
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          color={
                            client.days_since_last_message > 14
                              ? "error"
                              : "text.primary"
                          }
                        >
                          {client.days_since_last_message > 999
                            ? "90+ days"
                            : client.days_since_last_message > 90
                              ? "90+ days"
                              : `${client.days_since_last_message}d`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last Message
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
              {(data?.at_risk_clients?.length || 0) === 0 && !loading && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CheckIcon
                    sx={{ fontSize: 48, color: "success.main", mb: 2 }}
                  />
                  <Typography variant="h6" color="success.main">
                    No At-Risk Clients
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All clients are communicating well!
                  </Typography>
                </Box>
              )}
            </List>
          </ChartContainer>
        </Grid>
      </Grid>

      {/* Coach Follow-up Clients Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <ChartContainer
            title="Coach Follow-up Needed"
            subtitle={`${data?.coach_followup_clients?.length || 0} engaged clients need proactive coach check-in`}
            loading={loading}
            height={600}
          >
            <List
              sx={{ p: 0, maxHeight: "calc(100% - 40px)", overflowY: "auto" }}
            >
              {(data?.coach_followup_clients || []).map((client) => (
                <ListItem
                  key={client.client_id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1.5,
                    flexDirection: "column",
                    alignItems: "stretch",
                    backgroundColor: "background.paper",
                    "&:hover": {
                      backgroundColor: "action.hover",
                      cursor: "pointer",
                    },
                  }}
                  onClick={() =>
                    navigate(
                      `/home/clients/summary/${client.client_id}/${organizationId}`,
                    )
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      mb: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        mr: 2,
                        bgcolor: theme.palette.info.main,
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {client.first_name} {client.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {client.followup_reason}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Chip
                        label="Follow-up"
                        color="info"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Priority: {client.followup_priority}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Client metrics */}
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" color="info.main">
                          {client.combined_engagement_score}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Engagement Score
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" color="primary">
                          {client.communication_score}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Communication Score
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6">
                          {client.total_messages_sent}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Messages Sent
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" color="warning.main">
                          {client.days_since_last_message}d
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last Contact
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
              {(data?.coach_followup_clients?.length || 0) === 0 &&
                !loading && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <CheckIcon
                      sx={{ fontSize: 48, color: "success.main", mb: 2 }}
                    />
                    <Typography variant="h6" color="success.main">
                      All Engaged Clients Recently Contacted
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your engaged clients have all heard from their coaches
                      recently!
                    </Typography>
                  </Box>
                )}
            </List>
          </ChartContainer>
        </Grid>
      </Grid>

      {/* Actionable Insights */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ChartContainer
            title="Actionable Insights"
            subtitle="Priority actions to improve communication"
            loading={loading}
            height={300}
          >
            <List sx={{ p: 0 }}>
              {(data?.actionable_insights?.priority_actions || []).map(
                (action, index) => (
                  <ListItem key={index} sx={{ py: 1 }}>
                    <ListItemIcon>
                      <HighPriorityIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={action}
                      primaryTypographyProps={{
                        variant: "body1",
                        fontWeight: 500,
                      }}
                    />
                  </ListItem>
                ),
              )}
              {(data?.actionable_insights?.priority_actions?.length || 0) ===
                0 &&
                !loading && (
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <InfoIcon
                      sx={{ fontSize: 32, color: "info.main", mb: 1 }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      No urgent actions required. Your communication strategy is
                      working well!
                    </Typography>
                  </Box>
                )}
            </List>
          </ChartContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CommunicationModule;
